import { useSpotifyCallback } from '@church/data'
import { faSpotifyBrands } from '@church/ui-icons'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { z } from 'zod'
import { ErrorNote, Icon, Spinner } from '../components/ui.tsx'
import { urlOn } from '../lib/site.ts'

/**
 * Where Spotify sends a platform operator after they allow (or refuse) the
 * app for one of ForHim's own Spotify accounts — the redirect URI registered
 * with Spotify, on id.forhim.io. It hands what Spotify sent to the API once,
 * then goes back to Admin › Spotify, which says how it went.
 */
export const Route = createFileRoute('/_authed/spotify')({
  validateSearch: z.object({
    state: z.string().optional(),
    code: z.string().optional(),
    error: z.string().optional(),
  }),
  component: SpotifyReturnScreen,
})

function SpotifyReturnScreen() {
  const search = Route.useSearch()
  const complete = useSpotifyCallback()
  // A code works once: never send it twice (Strict Mode runs effects twice).
  const sent = useRef(false)

  useEffect(() => {
    if (sent.current || !search.state) return
    sent.current = true
    // The promise, not mutate's callbacks: those are dropped if the effect's
    // first run is torn down (Strict Mode) before the answer comes.
    complete
      .mutateAsync({
        state: search.state,
        ...(search.code && { code: search.code }),
        ...(search.error && { error: search.error }),
      })
      .then(({ outcome }) =>
        window.location.replace(urlOn('admin', `/admin/spotify?spotify=${outcome}`)),
      )
      .catch(() => undefined) // shown from complete.error
  }, [complete, search])

  return (
    <div className="flex min-h-full flex-col items-center justify-center bg-surface-sunken px-4 py-10">
      <div className="w-full max-w-md space-y-4 rounded-xl border border-border-subtle bg-surface-raised p-6 shadow-sm sm:p-8">
        <h1 className="flex items-center gap-2 text-xl font-semibold text-content-default">
          <Icon icon={faSpotifyBrands} className="size-6 text-success-solid" />
          Spotify
        </h1>
        {!search.state ? (
          <p className="text-sm text-content-muted">
            Nothing to connect here. Start from Admin › Spotify.
          </p>
        ) : complete.isError ? (
          <>
            <ErrorNote error={complete.error} />
            <a href={urlOn('admin', '/admin/spotify')} className="text-sm">
              Back to Admin › Spotify
            </a>
          </>
        ) : (
          <Spinner label="Connecting Spotify…" />
        )}
      </div>
    </div>
  )
}
